import React, { useState, useEffect } from 'react';
import { getUserLogins, revokeUserSessions, resetUserTotp, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { fmtDateTime } from '../utils/kernelFormat';

// "Chrome su Windows" ricavato dallo user agent: abbastanza per riconoscere un dispositivo, senza librerie.
export function describeBrowser(ua) {
  const s = String(ua || '');
  if (!s) return '—';
  const browser = /Edg\//.test(s) ? 'Edge'
    : /OPR\/|Opera/.test(s) ? 'Opera'
    : /Firefox\//.test(s) ? 'Firefox'
    : /Chrome\//.test(s) ? 'Chrome'
    : /Safari\//.test(s) ? 'Safari'
    : null;
  const os = /Windows/.test(s) ? 'Windows'
    : /Android/.test(s) ? 'Android'
    : /iPhone|iPad|iPod/.test(s) ? 'iOS'
    : /Mac OS X|Macintosh/.test(s) ? 'macOS'
    : /Linux/.test(s) ? 'Linux'
    : null;
  if (browser && os) return `${browser} · ${os}`;
  return browser || os || s.slice(0, 40);
}

/**
 * Storico degli accessi di un utente (riusciti e falliti). Usato da Utenti e da Utenti dell'azienda:
 * si mostra al posto dell'elenco, con il pulsante per tornare indietro.
 */
function KernelLoginHistory({ person, currentUserId, onClose }) {
  const { t } = useLanguage();
  const [logins, setLogins] = useState([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getUserLogins(person.id)
      .then((data) => {
        if (cancelled) return;
        setLogins(data.logins || []);
        setAvailable(data.available !== false);
      })
      .catch((err) => !cancelled && setError(errorMessage(err, t('loginHistory.errorLoad'))))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person.id]);

  const name = `${person.name || ''} ${person.surname || ''}`.trim();

  const handleResetTotp = async () => {
    if (!window.confirm(t('loginHistory.resetTotpConfirm', { name }))) return;
    setError('');
    setSuccess('');
    setRevoking(true);
    try {
      await resetUserTotp(person.id);
      setSuccess(t('loginHistory.resetTotpDone', { name }));
      person.totp_enabled = false;
    } catch (err) {
      setError(errorMessage(err, t('loginHistory.resetTotpError')));
    } finally {
      setRevoking(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm(t('loginHistory.revokeConfirm', { name }))) return;
    setError('');
    setSuccess('');
    setRevoking(true);
    try {
      await revokeUserSessions(person.id);
      setSuccess(t('loginHistory.revoked', { name }));
    } catch (err) {
      setError(errorMessage(err, t('loginHistory.revokeError')));
    } finally {
      setRevoking(false);
    }
  };
  const reasonLabel = (r) => (r ? t(`loginHistory.reason.${r}`) : '');

  return (
    <>
      <div className="page-header">
        <div>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
            {t('loginHistory.back')}
          </button>
          <h1 className="page-title" style={{ marginTop: 10 }}>{t('loginHistory.title', { name })}</h1>
          <p className="page-subtitle">{person.email}</p>
        </div>
        {Number(person.id) !== Number(currentUserId) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {person.totp_enabled && (
              <button type="button" className="btn btn-secondary" onClick={handleResetTotp} disabled={revoking} title={t('loginHistory.resetTotpHint')}>
                {t('loginHistory.resetTotp')}
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={handleRevoke} disabled={revoking} title={t('loginHistory.revokeHint')}>
              {revoking ? t('common.saving') : t('loginHistory.revoke')}
            </button>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      {loading && <div className="loading">{t('common.loading')}</div>}
      {!loading && !available && <div className="warning-message">{t('loginHistory.unavailable')}</div>}

      {!loading && available && (
        <div className="card">
          <div className="card-body table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('loginHistory.when')}</th>
                  <th>{t('loginHistory.result')}</th>
                  <th>{t('loginHistory.ip')}</th>
                  <th>{t('loginHistory.device')}</th>
                </tr>
              </thead>
              <tbody>
                {logins.map((l) => (
                  <tr key={l.id}>
                    <td className="nowrap">{fmtDateTime(l.created_at)}</td>
                    <td>
                      <span className={`badge ${Number(l.success) === 1 ? 'badge-success' : 'badge-warning'}`}>
                        {Number(l.success) === 1 ? t('loginHistory.ok') : t('loginHistory.failed')}
                      </span>
                      {Number(l.success) !== 1 && l.reason && <span style={{ opacity: 0.6 }}> {reasonLabel(l.reason)}</span>}
                    </td>
                    <td>{l.ip || '—'}</td>
                    <td title={l.user_agent || ''}>{describeBrowser(l.user_agent)}</td>
                  </tr>
                ))}
                {logins.length === 0 && (
                  <tr><td colSpan="4" className="empty-cell">{t('loginHistory.empty')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="card-body" style={{ paddingTop: 0 }}>
            <span style={{ opacity: 0.6 }}>{t('loginHistory.hint')}</span>
          </div>
        </div>
      )}
    </>
  );
}

export default KernelLoginHistory;
